/**
 * PostHog lazy-loaded façade.
 *
 * `posthog-js` is a heavy library (~150KB). We dynamic-import it only when
 * `initPostHog()` is called from the client, after hydration. Calls made
 * before init complete are queued and drained on first load.
 *
 * Type-only import keeps `posthog-js` out of the initial JS bundle.
 */

import type PostHogJs from "posthog-js";
import { redactCapture } from "./posthog-redact";

const POSTHOG_KEY = (process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "").trim();
// Default to the same-origin /ingest proxy (next.config rewrites) so
// ad-blockers can't eat capture requests. The env var still wins when set to
// an absolute host (e.g. local dev pointing straight at PostHog).
const POSTHOG_HOST = (process.env.NEXT_PUBLIC_POSTHOG_HOST || "/ingest").trim();

// Consent gate (dark by default). When NEXT_PUBLIC_CONSENT_BANNER !== 'true'
// every consent check short-circuits to "allowed", so behavior is byte-identical
// to no gate at all. When 'true', PostHog does not initialize until the user
// accepts; decline opts out and drops queued calls. Choice persists in
// localStorage.
const CONSENT_REQUIRED =
  (process.env.NEXT_PUBLIC_CONSENT_BANNER ?? "").trim() === "true";
const CONSENT_STORAGE_KEY = "vestra:analytics-consent";

type ConsentChoice = "accepted" | "declined";

function readConsent(): ConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === "accepted" || v === "declined" ? v : null;
  } catch {
    return null;
  }
}

/** True when analytics may run: consent not required, or the user accepted. */
function consentAllowsInit(): boolean {
  if (!CONSENT_REQUIRED) return true;
  return readConsent() === "accepted";
}

// One PostHog project takes production, staging and local traffic, so every
// web event says which deploy sent it. The per-env NEXT_PUBLIC_SITE_URL is the
// discriminator (robots.ts has why VERCEL_ENV is not: staging ships as a
// `--prod` deployment of the same Vercel project); `next dev` is always
// development. BUILD_COMMIT_SHA is the build-time sha next.config stamps for
// /api/version. Env is read at call time so a test can stub it.
const PRODUCTION_HOSTS = new Set([
  "vestra.ai",
  "www.vestra.ai",
  "bash.team",
  "www.bash.team",
]);
const STAGING_HOSTS = new Set(["staging.vestra.ai", "staging.bash.team"]);

function webEnvironment(): "production" | "staging" | "development" {
  if (process.env.NODE_ENV === "development") return "development";
  let host = "";
  try {
    host = new URL(process.env.NEXT_PUBLIC_SITE_URL ?? "").hostname;
  } catch {
    return "development";
  }
  if (PRODUCTION_HOSTS.has(host)) return "production";
  if (STAGING_HOSTS.has(host)) return "staging";
  return "development";
}

/** The super properties every web event carries: which environment, which build. */
export function releaseProperties(): {
  environment: string;
  release_sha: string;
} {
  return {
    environment: webEnvironment(),
    release_sha: process.env.BUILD_COMMIT_SHA || "unknown",
  };
}

type PostHogInstance = typeof PostHogJs;

let phInstance: PostHogInstance | null = null;
let initPromise: Promise<PostHogInstance | null> | null = null;
const callQueue: Array<(ph: PostHogInstance) => void> = [];
// A returning user who previously declined: drop calls instead of queuing them
// forever. Only ever true when the gate is enabled.
let consentDenied = CONSENT_REQUIRED && readConsent() === "declined";

function enqueue(fn: (ph: PostHogInstance) => void): void {
  if (phInstance) {
    fn(phInstance);
  } else if (!consentDenied) {
    callQueue.push(fn);
  }
}

/** Whether the dark consent banner is enabled for this build. */
export function isConsentBannerEnabled(): boolean {
  return CONSENT_REQUIRED;
}

/** The persisted consent choice, or null if the user hasn't chosen yet. */
export function getAnalyticsConsent(): ConsentChoice | null {
  return CONSENT_REQUIRED ? readConsent() : "accepted";
}

/** Grant consent: persist, then initialize PostHog (drains any queued calls). */
export function acceptAnalyticsConsent(): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, "accepted");
  } catch {
    /* private mode — best effort; init still proceeds for this session */
  }
  consentDenied = false;
  void initPostHog();
}

/** Decline consent: persist, opt out, and drop anything queued pre-choice. */
export function declineAnalyticsConsent(): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, "declined");
  } catch {
    /* private mode — best effort */
  }
  consentDenied = true;
  callQueue.length = 0;
  if (phInstance) {
    try {
      phInstance.opt_out_capturing();
    } catch {
      /* ignore */
    }
  }
}

export function initPostHog(): Promise<PostHogInstance | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (!POSTHOG_KEY) return Promise.resolve(null);
  // Hold off until consent is granted (no-op when the gate is disabled).
  if (!consentAllowsInit()) return Promise.resolve(null);
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const { default: posthogJs } = await import("posthog-js");
      posthogJs.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        // With a relative api_host (the /ingest proxy) posthog-js needs the
        // real UI origin for toolbar/deep links.
        ...(POSTHOG_HOST.startsWith("/")
          ? { ui_host: "https://us.posthog.com" }
          : {}),
        capture_pageview: false,
        capture_pageleave: true,
        persistence: "localStorage+cookie",
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "[data-ph-no-capture]",
        },
        autocapture: true,
        // Core Web Vitals autocapture (LCP/CLS/FCP/INP) → $web_vitals events,
        // person-joined like every other event. Separate from replay's
        // network_timing, so we opt in explicitly.
        capture_performance: { web_vitals: true },
        // Auto-capture unhandled window errors and promise rejections.
        // React render errors are captured manually via posthog.captureException
        // from DiagnosticBoundary so we can attach the boundary name + the
        // componentStack as event properties (componentStack is the only thing
        // that resolves to a real component name in production builds).
        capture_exceptions: true,
        // Credentials never ride a URL into an event: an OAuth ?code= or a
        // magic link's #access_token lands on a page that captures (#bugs 09-23).
        before_send: redactCapture,
      });
      posthogJs.register(releaseProperties());
      phInstance = posthogJs;
      const drain = callQueue.splice(0);
      for (const fn of drain) fn(posthogJs);
      return posthogJs;
    } catch (e) {
      console.warn("PostHog initialization failed:", e);
      return null;
    }
  })();

  return initPromise;
}

export function isPostHogReady(): boolean {
  return phInstance !== null;
}

/**
 * Façade exposing the subset of posthog-js we use across the app.
 * Calls before init are queued, calls after init pass through directly.
 */
export const posthog = {
  capture(event: string, properties?: Record<string, unknown>): void {
    enqueue((ph) => ph.capture(event, properties));
  },
  identify(userId: string, properties?: Record<string, unknown>): void {
    enqueue((ph) => ph.identify(userId, properties));
  },
  reset(): void {
    enqueue((ph) => {
      ph.reset();
      // reset() wipes super properties too; the deploy tag is not user data.
      ph.register(releaseProperties());
    });
  },
  captureException(error: unknown, properties?: Record<string, unknown>): void {
    enqueue((ph) => ph.captureException(error as Error, properties));
  },
};
